import React, { useState, useEffect, useRef, useCallback } from "react";
import { PageHeader } from "./DashboardLayout";
import { Copy, Check, Save, Loader2, Play, Pause, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, getErrorMessage } from "@/lib/api";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const COLOR_PRESETS = [
    { name: "Coral",    value: "#FF5B3E" },
    { name: "Electric", value: "#3D5BFF" },
    { name: "Mint",     value: "#2EE8B5" },
    { name: "Sunshine", value: "#FFD23F" },
    { name: "Ink",      value: "#0A0A0A" },
];

const SPEED_OPTIONS = [0.5, 1, 1.5, 2];
const BRANDING_PLANS = ["free", "starter", "basic"];
const PLAN_RANK = { free: 0, starter: 1, basic: 1, pro: 2, professional: 2, business: 2 };

// minRank: 0=free+, 1=starter+, 2=pro+
const PLAYER_STYLES = [
    { id: "default",  label: "Default",  desc: "Clean minimal bar",      minRank: 0 },
    { id: "wave",     label: "Wave",     desc: "Waveform visualization",  minRank: 1 },
    { id: "bars",     label: "Bars",     desc: "EQ bars animation",       minRank: 2 },
    { id: "minimal",  label: "Minimal",  desc: "Icon + time only",        minRank: 2 },
    { id: "gradient", label: "Gradient", desc: "Gradient fill pill",      minRank: 2 },
];

// ── Style thumbnails ──────────────────────────────────────────────────────────
function StyleThumb({ id, color, active }) {
    const c = active ? color : "#d4d0c8";
    const cm = active ? color : "#b0aca2";

    if (id === "default") return (
        <svg viewBox="0 0 80 32" className="w-full h-full">
            <circle cx="16" cy="16" r="11" fill={c}/>
            <polygon points="13,10 13,22 25,16" fill="white"/>
            <rect x="32" y="13" width="40" height="5" rx="2.5" fill={c} opacity="0.25"/>
            <rect x="32" y="13" width="18" height="5" rx="2.5" fill={c}/>
        </svg>
    );

    if (id === "wave") {
        const bars = Array.from({length: 28}, (_, i) => ({
            x: 5 + i * 2.6,
            h: 6 + Math.abs(Math.sin(i * 0.55 + 0.3)) * 16,
        }));
        return (
            <svg viewBox="0 0 80 32" className="w-full h-full">
                {bars.map((b, i) => (
                    <rect key={i} x={b.x} y={(32 - b.h) / 2} width="1.8" height={b.h} rx="0.9"
                        fill={i < 12 ? c : cm} opacity={i < 12 ? 1 : 0.4}/>
                ))}
            </svg>
        );
    }

    if (id === "bars") {
        const heights = [10, 18, 26, 14, 22, 8, 20, 16, 24, 12];
        return (
            <svg viewBox="0 0 80 32" className="w-full h-full">
                {heights.map((h, i) => (
                    <rect key={i} x={8 + i * 7} y={32 - h - 2} width="5" height={h} rx="2"
                        fill={i < 5 ? c : cm} opacity={i < 5 ? 1 : 0.45}/>
                ))}
            </svg>
        );
    }

    if (id === "minimal") return (
        <svg viewBox="0 0 80 32" className="w-full h-full">
            <circle cx="16" cy="16" r="12" fill={c}/>
            <polygon points="12,10 12,22 24,16" fill="white"/>
            <text x="34" y="21" fontFamily="monospace" fontSize="11" fill={c}>0:00</text>
        </svg>
    );

    if (id === "gradient") return (
        <svg viewBox="0 0 80 32" className="w-full h-full">
            <defs>
                <linearGradient id="sg" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={c}/>
                    <stop offset="100%" stopColor={c} stopOpacity="0.35"/>
                </linearGradient>
            </defs>
            <rect x="6" y="12" width="68" height="8" rx="4" fill={cm} opacity="0.3"/>
            <rect x="6" y="12" width="36" height="8" rx="4" fill="url(#sg)"/>
            <circle cx="42" cy="16" r="5" fill={c}/>
        </svg>
    );
    return null;
}

// ── Live preview player ───────────────────────────────────────────────────────
function PreviewPlayer({ style, color, theme, showTitle, showStatus, statusText, showDuration, playing, onPlayPause, progress, currentT, duration, onSeek }) {
    const isDark = theme === "dark";
    const textCls = isDark ? "text-cream-100" : "text-ink";
    const mutedCls = isDark ? "text-cream-100/60" : "text-ink-muted";
    const trackBg = isDark ? "rgba(255,255,255,0.18)" : "#ddd9d0";
    const fmt = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;

    const PlayBtn = () => (
        <button onClick={onPlayPause}
            className="w-10 h-10 rounded-full text-white flex items-center justify-center flex-shrink-0 hover:opacity-90 transition-opacity"
            style={{ background: color }}>
            {playing ? <Pause size={12} fill="currentColor"/> : <Play size={12} fill="currentColor" className="ml-0.5"/>}
        </button>
    );

    let body;
    if (style === "minimal") {
        body = (
            <div className="flex items-center gap-3">
                <PlayBtn/>
                <span className={`font-mono text-[13px] ${mutedCls}`}>{fmt(currentT)} / {fmt(duration || 0)}</span>
            </div>
        );
    } else if (style === "wave") {
        const count = 36;
        body = (
            <div className="flex items-center gap-2">
                <PlayBtn/>
                <div className="flex-1 flex items-end gap-[2px] h-9 cursor-pointer" onClick={onSeek}>
                    {Array.from({length: count}, (_, i) => {
                        const pct = (i / count) * 100;
                        const h = 10 + Math.abs(Math.sin(i * 0.55 + 0.3)) * 22;
                        return (
                            <div key={i} className="flex-1 rounded-full"
                                style={{ height: `${h}px`, background: pct <= progress ? color : trackBg }}/>
                        );
                    })}
                </div>
                <span className={`font-mono text-[11px] flex-shrink-0 ${mutedCls}`}>{fmt(currentT)} / {fmt(duration || 0)}</span>
            </div>
        );
    } else if (style === "bars") {
        const count = 22;
        body = (
            <div className="flex items-center gap-2">
                <PlayBtn/>
                <div className="flex-1 flex items-end gap-[3px] h-9 cursor-pointer" onClick={onSeek}>
                    {Array.from({length: count}, (_, i) => {
                        const pct = (i / count) * 100;
                        const h = 8 + ((i * 3 + 2) % 5) * 5;
                        return (
                            <div key={i} className="flex-1 rounded-t-sm"
                                style={{ height: `${h}px`, background: pct <= progress ? color : trackBg }}/>
                        );
                    })}
                </div>
                <span className={`font-mono text-[11px] flex-shrink-0 ${mutedCls}`}>{fmt(currentT)} / {fmt(duration || 0)}</span>
            </div>
        );
    } else if (style === "gradient") {
        body = (
            <div className="flex items-center gap-3">
                <PlayBtn/>
                <div className="flex-1 cursor-pointer py-2 relative" onClick={onSeek}>
                    <div className="h-3 rounded-full relative" style={{ background: trackBg }}>
                        <div className="h-full rounded-full" style={{
                            width: `${progress}%`,
                            background: `linear-gradient(to right, ${color}, ${color}66)`,
                        }}/>
                        <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow border-2"
                            style={{ left: `calc(${progress}% - 8px)`, borderColor: color }}/>
                    </div>
                </div>
                <span className={`font-mono text-[11px] flex-shrink-0 ${mutedCls}`}>{fmt(currentT)} / {fmt(duration || 0)}</span>
            </div>
        );
    } else {
        body = (
            <div className="flex items-center gap-3">
                <PlayBtn/>
                <div className="flex-1 cursor-pointer py-2" onClick={onSeek}>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: trackBg }}>
                        <div className="h-full rounded-full" style={{ width: `${progress}%`, background: color }}/>
                    </div>
                </div>
                <span className={`font-mono text-[11px] flex-shrink-0 ${mutedCls}`}>{fmt(currentT)} / {fmt(duration || 0)}</span>
            </div>
        );
    }

    return (
        <div className="space-y-1.5">
            {showTitle && <p className={`text-[13px] font-semibold ${textCls}`}>Your article title</p>}
            {showStatus && <p className={`text-[11.5px] ${mutedCls}`}>{statusText || "Ready to play"}</p>}
            <div className={`rounded-2xl p-4 ${isDark ? "bg-black/30" : "bg-cream-100 border border-cream-300"}`}>
                {body}
            </div>
            {showDuration && duration > 0 && (
                <p className={`text-[11px] ${mutedCls}`}>Duration: {fmt(duration)}</p>
            )}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Embed() {
    const { user } = useAuth();
    const plan = user?.plan || "free";
    const planRank = PLAN_RANK[plan] ?? 0;
    const showBranding = BRANDING_PLANS.includes(plan);
    const canCustomize = planRank >= 1; // starter+

    const [color,        setColor]        = useState("#FF5B3E");
    const [theme,        setTheme]        = useState("light");
    const [autoplay,     setAutoplay]     = useState(false);
    const [showTitle,    setShowTitle]    = useState(true);
    const [showStatus,   setShowStatus]   = useState(true);
    const [statusText,   setStatusText]   = useState("Ready to play");
    const [showDuration, setShowDuration] = useState(true);
    const [playerStyle,  setPlayerStyle]  = useState("default");
    const [copied,  setCopied]  = useState(false);
    const [saving,  setSaving]  = useState(false);
    const [loading, setLoading] = useState(true);
    const [dirty,   setDirty]   = useState(false);

    // Audio player
    const audioRef   = useRef(null);
    const [playing,  setPlaying]  = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentT, setCurrentT] = useState(0);
    const [speed,    setSpeed]    = useState(1);

    useEffect(() => {
        api.get("/settings/embed")
            .then(({ data }) => {
                if (data.color)   setColor(data.color);
                if (data.theme)   setTheme(data.theme);
                if (data.autoplay !== undefined)         setAutoplay(data.autoplay);
                if (data.show_title !== undefined)       setShowTitle(data.show_title);
                if (data.show_status_text !== undefined) setShowStatus(data.show_status_text);
                if (data.status_text)                    setStatusText(data.status_text);
                if (data.show_duration !== undefined)    setShowDuration(data.show_duration);
                if (data.player_style)                   setPlayerStyle(data.player_style);
                setDirty(false);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const change = (setter) => (val) => { setter(val); setDirty(true); };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put("/settings/embed", {
                color, theme, autoplay,
                show_title: showTitle,
                show_status_text: showStatus,
                status_text: statusText,
                show_duration: showDuration,
                player_style: playerStyle,
            });
            toast.success("Embed settings saved!");
            setDirty(false);
            confetti({ particleCount: 50, spread: 60, origin: { x: 0.5, y: 0.6 }, colors: [color, "#FF5B3E", "#2EE8B5"] });
        } catch (e) {
            toast.error(getErrorMessage(e) || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const initAudio = useCallback(() => {
        if (audioRef.current) return audioRef.current;
        const audio = new Audio("/demo.mp3");
        audio.onended       = () => { setPlaying(false); setProgress(0); setCurrentT(0); };
        audio.ontimeupdate  = () => {
            if (!audio.duration) return;
            setCurrentT(audio.currentTime);
            setProgress((audio.currentTime / audio.duration) * 100);
        };
        audio.onloadedmetadata = () => setDuration(audio.duration);
        audioRef.current = audio;
        return audio;
    }, []);

    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = speed;
    }, [speed]);

    useEffect(() => () => { audioRef.current?.pause(); }, []);

    const handlePlayPause = async () => {
        const audio = initAudio();
        if (playing) {
            audio.pause();
            setPlaying(false);
        } else {
            try {
                audio.playbackRate = speed;
                await audio.play();
                setPlaying(true);
            } catch { toast.error("Playback failed"); }
        }
    };

    const handleSeek = (e) => {
        const audio = initAudio();
        const rect  = e.currentTarget.getBoundingClientRect();
        const pct   = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        audio.currentTime = pct * (audio.duration || 0);
        setProgress(pct * 100);
    };

    const fmtTime = (s) => {
        const m   = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, "0")}`;
    };

    const code = `<script src="https://audo.dev/embed/v1.js"\n  data-article="YOUR_ARTICLE_ID"\n  data-color="${color}"\n  data-theme="${theme}"\n  data-style="${playerStyle}"${autoplay ? '\n  data-autoplay="true"' : ""}>\n</script>`;

    const onCopy = async () => {
        try { await navigator.clipboard.writeText(code); } catch {}
        setCopied(true);
        toast.success("Embed copied — paste into your site");
        confetti({ particleCount: 60, spread: 70, origin: { x: 0.5, y: 0.6 }, colors: ["#FF5B3E", "#3D5BFF", "#2EE8B5", "#FFD23F"] });
        setTimeout(() => setCopied(false), 2200);
    };

    if (loading) return (
        <div className="flex items-center gap-3 p-10 text-ink-muted">
            <Loader2 className="animate-spin" size={20}/> Loading embed settings…
        </div>
    );

    return (
        <div data-testid="embed-page">
            <PageHeader
                eyebrow="Embed widget"
                title="One line. Plays everywhere."
                subtitle="Customize the look, save your settings, copy the script into your CMS."
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* ── Left: Controls ── */}
                <div className="space-y-5">

                    {/* Color */}
                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-3">Player accent color</p>
                        <div className="grid grid-cols-5 gap-2 mb-3">
                            {COLOR_PRESETS.map(p => (
                                <button key={p.value} onClick={() => change(setColor)(p.value)}
                                    className={`aspect-square rounded-2xl border-2 transition-all ${color === p.value ? "border-ink scale-95" : "border-transparent hover:scale-105"}`}
                                    style={{ background: p.value }} title={p.name}/>
                            ))}
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl border border-cream-300 flex-shrink-0" style={{ background: color }}/>
                            <input type="text" value={color} onChange={e => change(setColor)(e.target.value)}
                                placeholder="#FF5B3E"
                                className="flex-1 px-3 py-2 border border-cream-300 rounded-xl text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-coral/40"
                                maxLength={7}/>
                        </div>
                    </div>

                    {/* Theme */}
                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-3">Theme</p>
                        <div className="grid grid-cols-2 gap-3">
                            {["light", "dark"].map(t => (
                                <button key={t} onClick={() => change(setTheme)(t)}
                                    className={`p-4 rounded-2xl border-2 text-[14px] font-semibold capitalize transition-all ${theme === t ? "border-coral bg-coral/5" : "border-cream-300 hover:bg-cream-100"}`}>
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Player style */}
                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted mb-4">Player style</p>
                        <div className="space-y-2">
                            {PLAYER_STYLES.map(s => {
                                const locked = planRank < s.minRank;
                                const isActive = playerStyle === s.id && !locked;
                                const lockLabel = s.minRank === 1 ? "Starter+" : "Pro+";
                                return (
                                    <button key={s.id}
                                        onClick={() => { if (!locked) change(setPlayerStyle)(s.id); }}
                                        disabled={locked}
                                        className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${
                                            isActive ? "border-coral bg-coral/5" :
                                            locked   ? "border-cream-200 opacity-60 cursor-not-allowed" :
                                                       "border-cream-300 hover:bg-cream-100"
                                        }`}>
                                        <div className="w-[72px] h-8 flex-shrink-0">
                                            <StyleThumb id={s.id} color={color} active={isActive}/>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[13px] font-semibold text-ink">{s.label}</p>
                                            <p className="text-[11px] text-ink-muted">{s.desc}</p>
                                        </div>
                                        {locked ? (
                                            <span className="flex-shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                                <Lock size={9}/> {lockLabel}
                                            </span>
                                        ) : isActive ? (
                                            <span className="flex-shrink-0 w-2 h-2 rounded-full bg-coral"/>
                                        ) : null}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Display options */}
                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">Display options</p>
                            {!canCustomize && (
                                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                    <Lock size={9}/> Starter+
                                </span>
                            )}
                        </div>
                        <div className="space-y-3">
                            {[
                                { label: "Show title",       value: showTitle,    setter: setShowTitle },
                                { label: "Show status text", value: showStatus,   setter: setShowStatus },
                                { label: "Show duration",    value: showDuration, setter: setShowDuration },
                            ].map(({ label, value, setter }) => (
                                <div key={label} className="flex items-center justify-between">
                                    <p className={`text-[13.5px] ${canCustomize ? "text-ink" : "text-ink-muted"}`}>{label}</p>
                                    <button
                                        onClick={() => { if (canCustomize) change(setter)(!value); }}
                                        disabled={!canCustomize}
                                        className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-coral" : "bg-cream-300"} disabled:cursor-not-allowed`}
                                        aria-pressed={value}>
                                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${value ? "translate-x-5" : ""}`}/>
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="mt-5">
                            <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">
                                "Ready to play" text
                            </label>
                            <input type="text" value={statusText}
                                onChange={e => { if (canCustomize) change(setStatusText)(e.target.value); }}
                                disabled={!canCustomize}
                                placeholder="Ready to play" maxLength={60}
                                className="w-full px-3 py-2.5 border border-cream-300 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:bg-cream-100 disabled:text-ink-muted disabled:cursor-not-allowed"
                            />
                        </div>
                    </div>

                    {/* Autoplay */}
                    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft flex items-center justify-between">
                        <div>
                            <p className="text-[14px] font-semibold text-ink">Autoplay</p>
                            <p className="text-[12px] text-ink-muted">Off by default. Most browsers block this.</p>
                        </div>
                        <button onClick={() => change(setAutoplay)(!autoplay)}
                            className={`relative w-11 h-6 rounded-full transition-colors ${autoplay ? "bg-coral" : "bg-cream-300"}`}
                            aria-pressed={autoplay}>
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${autoplay ? "translate-x-5" : ""}`}/>
                        </button>
                    </div>

                    {/* Save */}
                    <button onClick={handleSave} disabled={saving || !dirty}
                        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-coral text-white font-semibold text-[15px] hover:bg-coral/90 transition disabled:opacity-40">
                        {saving ? <><Loader2 size={16} className="animate-spin"/> Saving…</> : <><Save size={16}/> Save settings</>}
                    </button>
                    {!dirty && !saving && (
                        <p className="text-center text-[12px] text-ink-muted">Settings saved ✓</p>
                    )}
                </div>

                {/* ── Right: Preview + Code ── */}
                <div className="space-y-5">
                    {/* Live preview */}
                    <div className={`rounded-3xl p-6 border shadow-soft ${theme === "dark" ? "bg-ink-pill border-ink-pill" : "bg-white border-cream-300"}`}>
                        <p className={`text-[11px] uppercase tracking-[0.22em] font-semibold mb-4 ${theme === "dark" ? "text-cream-100/70" : "text-ink-muted"}`}>
                            Live preview
                        </p>
                        <PreviewPlayer
                            style={playerStyle} color={color} theme={theme}
                            showTitle={showTitle} showStatus={showStatus}
                            statusText={statusText} showDuration={showDuration}
                            playing={playing} onPlayPause={handlePlayPause}
                            progress={progress} currentT={currentT} duration={duration}
                            onSeek={handleSeek}
                        />
                        {/* Speed controls */}
                        <div className="flex items-center gap-1.5 mt-4">
                            <span className={`text-[10px] mr-0.5 ${theme === "dark" ? "text-cream-100/40" : "text-ink-muted"}`}>Speed</span>
                            {SPEED_OPTIONS.map(s => (
                                <button key={s} onClick={() => setSpeed(s)}
                                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold transition-all ${
                                        speed === s ? "text-white" : theme === "dark" ? "text-cream-100/60 hover:text-cream-100" : "text-ink-muted hover:text-ink"
                                    }`}
                                    style={speed === s ? { background: color } : {}}>
                                    {s}x
                                </button>
                            ))}
                        </div>
                        {showBranding && (
                            <>
                                <div className="mt-3 flex justify-end">
                                    <a href="https://audo.dev" target="_blank" rel="noopener noreferrer"
                                        className={`text-[10px] font-semibold opacity-50 hover:opacity-100 transition-opacity ${theme === "dark" ? "text-cream-100" : "text-ink"}`}>
                                        🎵 Made with Audo.dev
                                    </a>
                                </div>
                                <p className={`mt-2 text-[11px] ${theme === "dark" ? "text-cream-100/40" : "text-ink-muted"}`}>
                                    Upgrade to Pro to remove the "Made with Audo.dev" badge.
                                </p>
                            </>
                        )}
                    </div>

                    {/* Code block */}
                    <div className="code-block p-5 sm:p-6 relative">
                        <pre className="text-[13px] whitespace-pre-wrap break-all overflow-x-auto" data-testid="embed-code">
                            <code>{code}</code>
                        </pre>
                        <button onClick={onCopy} data-testid="embed-copy-btn"
                            className={`absolute top-4 right-4 px-3.5 py-2 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 transition-all ${copied ? "bg-mint text-ink" : "bg-cream-100 text-ink hover:bg-coral hover:text-white"}`}>
                            {copied ? <><Check size={13}/> Copied</> : <><Copy size={13}/> Copy</>}
                        </button>
                    </div>

                    <div className="bg-cream-200 border border-cream-300 rounded-3xl p-5">
                        <p className="text-[12.5px] text-ink-muted leading-relaxed">
                            <span className="font-semibold text-ink">How to use:</span> Go to your <strong>Articles</strong> page and copy the embed code next to each article — it includes the correct <code className="bg-white px-1.5 py-0.5 rounded font-mono text-[11px]">data-article</code> ID. Replace <code className="bg-white px-1.5 py-0.5 rounded font-mono text-[11px]">YOUR_ARTICLE_ID</code> above with that ID, then paste the full script before the closing <code className="bg-white px-1.5 py-0.5 rounded font-mono text-[11px]">{"</body>"}</code> tag in your CMS.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
